import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { axiosErrorHandler } from 'common/helpers/axios.helper';
import { AppDispatch, RootState } from 'app/store';
import {
  IWorkspaceListItem,
  IWorkspaceListParams,
  IWorkspace,
  ICreateWorkspacePayload,
  IUpdateWorkspacePayload,
  ICopyWorkspacePayload,
  IDeleteWorkspacePayload,
  IGroupedWorkspace,
  IWorkspacePosition,
  ICreateWorkspacePositionPayload,
  IUpdateWorkspacePositionPayload,
  IDeleteWorkspacePositionPayload,
} from 'entities/Workspace/Workspace.models';
import { workspaceTransport } from 'entities/Workspace/Workspace.transport';
import {
  addCurrentUserWorkspacesItem,
  patchCurrentUserWorkspacesItem,
  removeCurrentUserWorkspacesItem,
} from 'entities/User/User.slice';
import {
  decreaseGroupedWorkspaceCatalogPositionsCountByCategoryId,
  increaseGroupedWorkspaceCatalogPositionsCountByCategoryId,
} from 'entities/Workspace/Workspace.helper';

interface IState {
  workspace: IWorkspace | null;
  workspaceList: IWorkspaceListItem[];
  workspaceCount: number;
  isWorkspaceLoading: boolean;
  workspaceError: string | null;
  groupedWorkspace: IGroupedWorkspace | null;
  workspacePosition: IWorkspacePosition | null;
  workspacePositionError: string | null;
}

const initialState: IState = {
  workspace: null,
  workspaceList: [],
  workspaceCount: 0,
  isWorkspaceLoading: false,
  workspaceError: null,
  groupedWorkspace: null,
  workspacePosition: null,
  workspacePositionError: null,
};

export const workspaceSlice = createSlice({
  name: 'workspace',
  initialState,
  reducers: {
    setWorkspace(state, action: PayloadAction<IWorkspace | null>) {
      state.workspace = action.payload;
    },
    setWorkspaceList(state, action: PayloadAction<IWorkspaceListItem[]>) {
      state.workspaceList = action.payload;
    },
    setWorkspaceCount(state, action: PayloadAction<number>) {
      state.workspaceCount = action.payload;
    },
    setIsWorkspaceLoading(state, action: PayloadAction<boolean>) {
      state.isWorkspaceLoading = action.payload;
    },
    setWorkspaceError(state, action: PayloadAction<string | null>) {
      state.workspaceError = action.payload;
    },
    addWorkspaceListItem(state, action: PayloadAction<IWorkspace>) {
      state.workspaceList.push(action.payload);
    },
    patchWorkspaceListItem(state, action: PayloadAction<IWorkspace>) {
      const item = state.workspaceList.find((i) => i.id === action.payload.id);

      if (!item) {
        return;
      }

      Object.assign(item, action.payload);
    },
    removeWorkspaceListItem(state, action: PayloadAction<number>) {
      state.workspaceList = state.workspaceList.filter((item) => item.id !== action.payload);
    },
    setGroupedWorkspace(state, action: PayloadAction<IGroupedWorkspace | null>) {
      state.groupedWorkspace = action.payload;
    },
    patchGroupedWorkspace(state, action: PayloadAction<IWorkspace>) {
      if (!state.groupedWorkspace) {
        return;
      }

      state.groupedWorkspace.workspace = action.payload;
    },
    addGroupedWorkspacePosition(state, action: PayloadAction<IWorkspacePosition>) {
      if (!state.groupedWorkspace) {
        return;
      }

      const category = state.groupedWorkspace.categories.find((item) => item.categoryId === action.payload.categoryId);

      if (!category) {
        return;
      }

      category.positions.push(action.payload);
    },
    patchGroupedWorkspacePosition(state, action: PayloadAction<IWorkspacePosition>) {
      if (!state.groupedWorkspace) {
        return;
      }

      const category = state.groupedWorkspace.categories.find((item) => item.categoryId === action.payload.categoryId);

      if (!category) {
        return;
      }

      const position = category.positions.find((item) => item.id === action.payload.id);

      if (!position) {
        return;
      }

      Object.assign(position, action.payload);
    },
    deleteGroupedWorkspacePosition(state, action: PayloadAction<IWorkspacePosition>) {
      if (!state.groupedWorkspace) {
        return;
      }

      const category = state.groupedWorkspace.categories.find((item) => item.categoryId === action.payload.categoryId);

      if (!category) {
        return;
      }

      category.positions = category.positions.filter((item) => item.id !== action.payload.id);
    },
    decreaseGroupedWorkspacePositionsCount(state, action: PayloadAction<number>) {
      if (!state.groupedWorkspace) {
        return;
      }

      state.groupedWorkspace.catalog = decreaseGroupedWorkspaceCatalogPositionsCountByCategoryId(
        state.groupedWorkspace.catalog,
        action.payload,
      );
    },
    increaseGroupedWorkspacePositionsCount(state, action: PayloadAction<number>) {
      if (!state.groupedWorkspace) {
        return;
      }

      state.groupedWorkspace.catalog = increaseGroupedWorkspaceCatalogPositionsCountByCategoryId(
        state.groupedWorkspace.catalog,
        action.payload,
      );
    },
    setWorkspacePosition(state, action: PayloadAction<IWorkspacePosition | null>) {
      state.workspacePosition = action.payload;
    },
    setWorkspacePositionError(state, action: PayloadAction<string | null>) {
      state.workspacePositionError = action.payload;
    },
  },
});

export const {
  setWorkspace,
  setWorkspaceList,
  setWorkspaceCount,
  setIsWorkspaceLoading,
  setWorkspaceError,
  addWorkspaceListItem,
  patchWorkspaceListItem,
  removeWorkspaceListItem,
  setGroupedWorkspace,
  patchGroupedWorkspace,
  addGroupedWorkspacePosition,
  patchGroupedWorkspacePosition,
  deleteGroupedWorkspacePosition,
  decreaseGroupedWorkspacePositionsCount,
  increaseGroupedWorkspacePositionsCount,
  setWorkspacePosition,
  setWorkspacePositionError,
} = workspaceSlice.actions;

export const selectWorkspace = (state: RootState) => state.workspace.workspace;
export const selectWorkspaceList = (state: RootState) => state.workspace.workspaceList;
export const selectWorkspaceCount = (state: RootState) => state.workspace.workspaceCount;
export const selectIsWorkspaceLoading = (state: RootState) => state.workspace.isWorkspaceLoading;
export const selectWorkspaceError = (state: RootState) => state.workspace.workspaceError;
export const selectGroupedWorkspace = (state: RootState) => state.workspace.groupedWorkspace;
export const selectWorkspacePosition = (state: RootState) => state.workspace.workspacePosition;
export const selectWorkspacePositionError = (state: RootState) => state.workspace.workspacePositionError;

export const workspaceReducer = workspaceSlice.reducer;

export const getWorkspaceList = (params: IWorkspaceListParams) => async (dispatch: AppDispatch) => {
  dispatch(setIsWorkspaceLoading(true));

  try {
    const response = await workspaceTransport.get(params);
    dispatch(setWorkspaceList(response.data));
    dispatch(setWorkspaceCount(response.count));
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsWorkspaceLoading(false));
  }
};

export const createWorkspace = (payload: ICreateWorkspacePayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsWorkspaceLoading(true));

  try {
    const response = await workspaceTransport.create(payload);
    dispatch(setWorkspace(response));
    dispatch(addWorkspaceListItem(response));
    dispatch(addCurrentUserWorkspacesItem(response));
    payload.onSuccess?.();
    return response;
  } catch (error) {
    axiosErrorHandler(error, setWorkspaceError);
    return null;
  } finally {
    dispatch(setIsWorkspaceLoading(false));
  }
};

export const updateWorkspace = (payload: IUpdateWorkspacePayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsWorkspaceLoading(true));

  try {
    const response = await workspaceTransport.update(payload);
    dispatch(setWorkspace(response));
    dispatch(patchWorkspaceListItem(response));
    dispatch(patchGroupedWorkspace(response));
    dispatch(patchCurrentUserWorkspacesItem(response));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error, setWorkspaceError);
  } finally {
    dispatch(setIsWorkspaceLoading(false));
  }
};

export const copyWorkspace = (payload: ICopyWorkspacePayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsWorkspaceLoading(true));

  try {
    const response = await workspaceTransport.copy(payload);
    dispatch(setWorkspace(response));
    dispatch(addWorkspaceListItem(response));
    dispatch(addCurrentUserWorkspacesItem(response));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error, setWorkspaceError);
  } finally {
    dispatch(setIsWorkspaceLoading(false));
  }
};

export const deleteWorkspace = (payload: IDeleteWorkspacePayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsWorkspaceLoading(true));

  try {
    const response = await workspaceTransport.delete(payload);
    dispatch(removeWorkspaceListItem(response.id));
    dispatch(removeCurrentUserWorkspacesItem(response));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsWorkspaceLoading(false));
  }
};

export const getGroupedWorkspaceById = (id: number) => async (dispatch: AppDispatch) => {
  dispatch(setIsWorkspaceLoading(true));

  try {
    const response = await workspaceTransport.getGroupedWorkspaceById(id);
    dispatch(setGroupedWorkspace(response));
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsWorkspaceLoading(false));
  }
};

export const getWorkspacePositionById = (id: number) => async (dispatch: AppDispatch) => {
  dispatch(setIsWorkspaceLoading(true));

  try {
    const response = await workspaceTransport.getPositionById(id);
    dispatch(setWorkspacePosition(response));
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsWorkspaceLoading(false));
  }
};

export const createWorkspacePosition = (payload: ICreateWorkspacePositionPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsWorkspaceLoading(true));

  try {
    const response = await workspaceTransport.createPosition(payload);
    dispatch(addGroupedWorkspacePosition(response));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error, setWorkspacePositionError);
  } finally {
    dispatch(setIsWorkspaceLoading(false));
  }
};

export const updateWorkspacePosition = (payload: IUpdateWorkspacePositionPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsWorkspaceLoading(true));

  try {
    const response = await workspaceTransport.updatePosition(payload);
    dispatch(patchGroupedWorkspacePosition(response));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error, setWorkspacePositionError);
  } finally {
    dispatch(setIsWorkspaceLoading(false));
  }
};

export const deleteWorkspacePosition = (payload: IDeleteWorkspacePositionPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsWorkspaceLoading(true));

  try {
    const response = await workspaceTransport.deletePosition(payload);
    dispatch(deleteGroupedWorkspacePosition(response));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsWorkspaceLoading(false));
  }
};
